'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { Play } from 'lucide-react'
import { youtubeEmbedUrl, youtubeThumbnailUrl } from '@/lib/youtube'

/**
 * Quieter player for `autoplayWhenVisible` and `loadOnPageLoad`: related
 * videos from our own channel only, no annotations, inline playback on iOS
 * (all documented player parameters). YouTube always shows the title, channel and logo, and its
 * terms forbid covering any part of the player, so those stay.
 */
const QUIET_PLAYER_PARAMS = 'rel=0&iv_load_policy=3&playsinline=1'

/**
 * Browsers only autoplay muted media. `mute=1` is not in YouTube's documented
 * parameter list (the documented route, `player.mute()`, needs YouTube's
 * script allowed in our CSP); if YouTube ever ignores it, the browser blocks
 * the autoplay and the visitor presses play, as with a plain facade.
 */
const MUTED_AUTOPLAY_PARAMS = 'autoplay=1&mute=1'

/**
 * Click-to-play YouTube embed. Until the visitor presses play, the page shows
 * the video's thumbnail through our own image optimizer, so a page view never
 * contacts YouTube. The consent manager governs analytics only; this is what
 * keeps an embed from calling a third party before the visitor asks for it.
 *
 * `autoplayWhenVisible` is the one exception: the player loads and starts
 * muted once more than half of it is on screen (YouTube's autoplay rule), and
 * never for visitors who prefer reduced motion. YouTube allows one
 * autoplaying player per page, so set it on one player at most.
 *
 * `loadOnPageLoad` renders the player itself in the server HTML, never
 * playing until the visitor presses play. Only a video's own watch page
 * (/videos/<slug>) sets it: Google indexes a video only when its player is
 * on the page at load ("Don't rely on user actions ... to load the video"),
 * and the watch page is the one page each video is meant to be indexed on.
 * The cost is that a watch-page view contacts YouTube (privacy-mode host).
 *
 * Fills its parent: give the parent `relative aspect-video`.
 */
export default function YouTubeFacade({
  id,
  title,
  sizes,
  autoplayWhenVisible = false,
  loadOnPageLoad = false,
}: {
  /** The YouTube id and title only: never pass a whole registry entry, which
   *  would serialize its transcript into the page payload. */
  id: string
  title: string
  sizes: string
  autoplayWhenVisible?: boolean
  loadOnPageLoad?: boolean
}) {
  const [start, setStart] = useState<'page' | 'click' | 'visible' | null>(
    loadOnPageLoad ? 'page' : null
  )
  const facadeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const facade = facadeRef.current
    if (!autoplayWhenVisible || !facade) return
    if (typeof IntersectionObserver === 'undefined') return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.intersectionRatio > 0.5)) {
          observer.disconnect()
          setStart(current => current ?? 'visible')
        }
      },
      // YouTube requires MORE than half on screen; 0.51 fires just past it.
      { threshold: [0.5, 0.51] }
    )
    observer.observe(facade)
    return () => observer.disconnect()
  }, [autoplayWhenVisible])

  if (start) {
    const params = [
      start === 'visible' ? MUTED_AUTOPLAY_PARAMS : null,
      start === 'click' ? 'autoplay=1' : null,
      autoplayWhenVisible || loadOnPageLoad ? QUIET_PLAYER_PARAMS : null,
    ]
      .filter(Boolean)
      .join('&')

    // globals.css strips the focus ring from iframes, so the wrapper shows
    // where focus is, drawn inside the frame so a clipping parent keeps it.
    return (
      <div className='absolute inset-0 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-ring'>
        <iframe
          className='h-full w-full'
          src={`${youtubeEmbedUrl(id)}?${params}`}
          title={title}
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          referrerPolicy='strict-origin-when-cross-origin'
          allowFullScreen
        />
      </div>
    )
  }

  return (
    <button
      ref={facadeRef}
      type='button'
      onClick={() => setStart('click')}
      aria-label={`Play video: ${title}`}
      className='group absolute inset-0 h-full w-full cursor-pointer'
    >
      <Image
        src={youtubeThumbnailUrl(id)}
        alt=''
        fill
        sizes={sizes}
        className='object-cover'
      />
      <span
        aria-hidden='true'
        className='absolute inset-0 flex items-center justify-center'
      >
        <span className='flex h-16 w-16 items-center justify-center bg-[var(--cta-fill)] text-white transition-transform duration-200 group-hover:scale-110 motion-reduce:transition-none motion-reduce:group-hover:scale-100'>
          <Play size={24} fill='currentColor' />
        </span>
      </span>
    </button>
  )
}
